import { AGGClient } from 'src/clients/agg-client/agg-client';

const createQuery = (accountId: string) => {
	return `#graphql
    query InboundSalesQuery {
        growthUnifiedProfile_getUnifiedProfile(accountId: "${accountId}"){
            userProfile{
                domainType
                smbUserVisitedPricingPages
            }
            sites{
                cloudId
                displayName
                products{
                    productKey
                    productName
                    productEdition
                }
            }
        }
    }
`;
};

/**
 * Builds the headers every GUPS sales-inbound lookup needs.
 *
 * X-Slauth-User-Context-Account-Id is only for local testing. In a deployed
 * environment, the service proxy translates the user context token into this
 * header.
 */
const buildSalesInboundHeaders = (accountId: string): { [key: string]: string } => {
	const customHeaders: { [key: string]: string } = {
		'x-use-case': 'sales-inbound',
	};

	if (process.env['NODE_ENV'] === 'development') {
		customHeaders['X-Slauth-User-Context-Account-Id'] = accountId;
	}

	return customHeaders;
};

interface GetIsSMBSalesLeadProps {
	accountId: string;
	aggClient?: AGGClient;
}

type InboundSalesQueryResponse = {
	errors: Array<{
		message: string;
	}>;
	data: {
		growthUnifiedProfile_getUnifiedProfile: {
			sites: Array<{
				cloudId: string;
				displayName: string;
				products: Array<{
					productEdition: string;
					productKey: string;
					productName: string;
				}>;
			}>;
			userProfile: {
				domainType: string;
				smbUserVisitedPricingPages: boolean;
			};
		};
	};
};

export const getIsSMBSalesLead = async ({
	accountId,
	aggClient = new AGGClient(),
}: GetIsSMBSalesLeadProps): Promise<boolean> => {
	const { data, errors } = await aggClient.query<InboundSalesQueryResponse>(
		createQuery(accountId),
		undefined,
		buildSalesInboundHeaders(accountId),
	);

	if (errors) {
		console.error(`GRS API failed`, errors[0]?.message);
		throw new Error(errors[0]?.message);
	}

	const isSMBSalesLead =
		data?.growthUnifiedProfile_getUnifiedProfile?.userProfile?.smbUserVisitedPricingPages;

	return isSMBSalesLead;
};

/**
 * `accountId` is passed as a GraphQL variable rather than interpolated into the
 * document, so it goes through the variables mechanism instead of string
 * concatenation. (`createQuery` above still interpolates; that is the shipped
 * SMB path and is deliberately left alone here.)
 *
 * The variable is typed `ID!`, not `String!`. `growthUnifiedProfile_getAccountProfile`
 * is absent from `graphql-types.generated.ts`, so the argument type cannot be
 * confirmed from the generated schema — but the sibling
 * `QueryGrowthUnifiedProfile_GetUnifiedProfileArgs` types the same `accountId`
 * argument as `InputMaybe<Scalars['ID']>`. A non-null `ID!` is a valid usage
 * wherever either `ID` or `ID!` is expected, so it holds in both cases, whereas
 * `String!` would fail validation against an `ID` argument. That failure mode is
 * silent: validation errors surface as `errors`, which this helper throws on, the
 * caller swallows, and the CTA then never renders for anyone.
 */
const ACCOUNT_PROFILE_QUERY = `#graphql
    query InboundEnterpriseSalesQuery($accountId: ID!) {
        growthUnifiedProfile_getAccountProfile(accountId: $accountId){
            companyProfile{
                accountSegment
            }
        }
    }
`;

/**
 * Account segments that receive the Enterprise "Contact us" CTA.
 *
 * GUPS returns exactly one of: Strategic | SMB Plus | Mid-Market | SMB |
 * Government | Enterprise. Confirmed with the GUPS team on 2026-09-18; these
 * literals match the warehouse `dim_company.account_segment` vocabulary, so no
 * mapping layer is required.
 *
 * Do NOT confuse this with `enterprise_account_status`
 * (Platinum / GAM / Gold / Bronze / ...), which is Enterprise *tiering* rather
 * than segment and is a different field entirely.
 */
const ENTERPRISE_ACCOUNT_SEGMENTS = ['Enterprise', 'Strategic'];

interface GetIsEnterpriseSalesLeadProps {
	accountId: string;
	aggClient?: AGGClient;
}

/**
 * Hand-written because the generated schema does not yet know these fields:
 * `GrowthUnifiedProfileCompanyProfile` in graphql-types.generated.ts declares
 * only `companyType`. This mirrors the precedent set by
 * `InboundSalesQueryResponse` above — the query is not type-checked against the
 * schema until a regeneration lands.
 */
type InboundEnterpriseSalesQueryResponse = {
	errors: Array<{
		message: string;
	}>;
	data: {
		growthUnifiedProfile_getAccountProfile: {
			companyProfile: {
				accountSegment: string | null;
			};
		};
	};
};

export const getIsEnterpriseSalesLead = async ({
	accountId,
	aggClient = new AGGClient(),
}: GetIsEnterpriseSalesLeadProps): Promise<boolean> => {
	const { data, errors } = await aggClient.query<InboundEnterpriseSalesQueryResponse>(
		ACCOUNT_PROFILE_QUERY,
		{ accountId },
		buildSalesInboundHeaders(accountId),
	);

	if (errors) {
		console.error(`GRS API failed`, errors[0]?.message);
		throw new Error(errors[0]?.message);
	}

	const accountSegment =
		data?.growthUnifiedProfile_getAccountProfile?.companyProfile?.accountSegment;

	// A missing or unrecognised segment is not eligible. Deliberately no
	// fallback to annualRevenue or any other signal — inferring the segment
	// would silently redefine the experiment audience mid-flight.
	if (!accountSegment) {
		return false;
	}

	return ENTERPRISE_ACCOUNT_SEGMENTS.includes(accountSegment);
};

/**
 * The segment alone decides enrolment, so this reads only the account profile.
 * The shipped SMB CTA's `smbUserVisitedPricingPages` flag is deliberately not
 * read: the users it serves are enrolled like everyone else, and the shipped
 * path still reads the flag itself. `$accountId: ID!` for the reason given at
 * ACCOUNT_PROFILE_QUERY.
 */
const CONFLUENCE_TOUR_SMB_SALES_QUERY = `#graphql
    query InboundConfluenceTourSmbSalesQuery($accountId: ID!) {
        growthUnifiedProfile_getAccountProfile(accountId: $accountId){
            companyProfile{
                accountSegment
            }
        }
    }
`;

/**
 * Account segments enrolled in the Confluence product tour experiment. Same
 * GUPS vocabulary as ENTERPRISE_ACCOUNT_SEGMENTS; note the spelling 'SMB Plus',
 * not 'SMB+'.
 */
const CONFLUENCE_TOUR_SMB_ACCOUNT_SEGMENTS = ['SMB', 'SMB Plus'];

interface GetIsConfluenceTourSmbSalesLeadProps {
	accountId: string;
	aggClient?: AGGClient;
}

/**
 * Hand-written for the same reason as InboundEnterpriseSalesQueryResponse.
 */
type InboundConfluenceTourSmbSalesQueryResponse = {
	errors: Array<{
		message: string;
	}>;
	data: {
		growthUnifiedProfile_getAccountProfile: {
			companyProfile: {
				accountSegment: string | null;
			};
		};
	};
};

/**
 * Eligibility for the Confluence product tour experiment (TWCG-2633): any SMB or
 * SMB Plus account.
 *
 * That includes the users the shipped SMB CTA already serves
 * (`smbUserVisitedPricingPages`). They are enrolled in both arms and see the
 * shipped CTA in either, so control is today's experience for the whole
 * segment, and the readout is the effect of showing the CTA to all of it.
 */
export const getIsConfluenceTourSmbSalesLead = async ({
	accountId,
	aggClient = new AGGClient(),
}: GetIsConfluenceTourSmbSalesLeadProps): Promise<boolean> => {
	const { data, errors } = await aggClient.query<InboundConfluenceTourSmbSalesQueryResponse>(
		CONFLUENCE_TOUR_SMB_SALES_QUERY,
		{ accountId },
		buildSalesInboundHeaders(accountId),
	);

	// Any error fails the lookup, even alongside partial data.
	if (errors) {
		console.error(`GRS API failed`, errors[0]?.message);
		throw new Error(errors[0]?.message);
	}

	const accountSegment =
		data?.growthUnifiedProfile_getAccountProfile?.companyProfile?.accountSegment;

	// As for Enterprise: a missing or unrecognised segment is not eligible, with
	// no fallback to any other signal.
	if (!accountSegment) {
		return false;
	}

	return CONFLUENCE_TOUR_SMB_ACCOUNT_SEGMENTS.includes(accountSegment);
};

/**
 * The Jira product tour experiment's lookup. Like the Confluence tour's, it
 * reads only the account profile: the segment alone decides enrolment, so users
 * the shipped SMB CTA serves are enrolled too. Its own operation name keeps the
 * two experiments' lookups separable in gateway metrics.
 */
const JIRA_TOUR_SMB_SALES_QUERY = `#graphql
    query InboundJiraTourSmbSalesQuery($accountId: ID!) {
        growthUnifiedProfile_getAccountProfile(accountId: $accountId){
            companyProfile{
                accountSegment
            }
        }
    }
`;

/**
 * Account segments enrolled in the Jira product tour experiment. The same
 * audience as the Confluence tour, kept separate so each experiment owns its
 * targeting.
 */
const JIRA_TOUR_SMB_ACCOUNT_SEGMENTS = ['SMB', 'SMB Plus'];

interface GetIsJiraTourSmbSalesLeadProps {
	accountId: string;
	aggClient?: AGGClient;
}

/**
 * Hand-written for the same reason as InboundEnterpriseSalesQueryResponse.
 */
type InboundJiraTourSmbSalesQueryResponse = {
	errors: Array<{
		message: string;
	}>;
	data: {
		growthUnifiedProfile_getAccountProfile: {
			companyProfile: {
				accountSegment: string | null;
			};
		};
	};
};

/**
 * Eligibility for the Jira product tour experiment: any SMB or SMB Plus
 * account, including the users the shipped SMB CTA already serves
 * (`smbUserVisitedPricingPages`). They are enrolled in both arms and see the
 * shipped CTA in either, so control is today's experience for the whole
 * segment.
 */
export const getIsJiraTourSmbSalesLead = async ({
	accountId,
	aggClient = new AGGClient(),
}: GetIsJiraTourSmbSalesLeadProps): Promise<boolean> => {
	const { data, errors } = await aggClient.query<InboundJiraTourSmbSalesQueryResponse>(
		JIRA_TOUR_SMB_SALES_QUERY,
		{ accountId },
		buildSalesInboundHeaders(accountId),
	);

	// Any error fails the lookup, even alongside partial data.
	if (errors) {
		console.error(`GRS API failed`, errors[0]?.message);
		throw new Error(errors[0]?.message);
	}

	const accountSegment =
		data?.growthUnifiedProfile_getAccountProfile?.companyProfile?.accountSegment;

	// A missing or unrecognised segment is not eligible, with no fallback to any
	// other signal.
	if (!accountSegment) {
		return false;
	}

	return JIRA_TOUR_SMB_ACCOUNT_SEGMENTS.includes(accountSegment);
};
